import { useQueryClient } from "@tanstack/react-query";
import type { PreOrder, PreOrderFlow } from "@/app/types";
import type { PartnerResponse } from "@/app/api/types/partner";
import type { ProductResponse } from "@/app/api/types/product";
import type { LocationResponse } from "@/app/api/types/location";
import type { UnitResponse } from "@/app/api/types/unit";
import { UUID } from "crypto";
import { partnerKeys } from "../../keys/partners";
import { unitKeys } from "../../keys/units";
import { employeeKeys } from "../../keys/employees";
import { preOrderKeys } from "./keys";

/**
 * Helper function to cache related entities from pre-order data
 * Also enriches pre-order with cached partner and employee data if available
 */
export function cachePreOrderRelatedData(
  queryClient: ReturnType<typeof useQueryClient>,
  preOrder: PreOrder,
) {
  // If pre-order has partner_id but no partner object, try to get from cache
  if (preOrder.partner_id && !preOrder.partner) {
    const cachedPartner = queryClient.getQueryData(
      partnerKeys.detail(preOrder.partner_id as UUID),
    );
    if (cachedPartner) {
      preOrder.partner = cachedPartner as any;
    }
  }

  // Cache partner if present in pre-order
  if (preOrder.partner) {
    queryClient.setQueryData(
      partnerKeys.detail(preOrder.partner.id as UUID),
      preOrder.partner,
    );
  }

  // If pre-order has shipping_address_id but no shipping_address, derive from partner.addresses
  if (preOrder.shipping_address_id && !preOrder.shipping_address) {
    const partner = preOrder.partner as PartnerResponse | undefined;
    const addr = partner?.addresses?.find(
      (a: any) => a.id === preOrder.shipping_address_id,
    );
    if (addr) {
      preOrder.shipping_address = addr;
    }
  }

  // If pre-order has employee_id but no employee object, try to get from cache
  if (preOrder.employee_id && !preOrder.employee) {
    const cachedEmployee = queryClient.getQueryData(
      employeeKeys.detail(preOrder.employee_id as UUID),
    );
    if (cachedEmployee) {
      preOrder.employee = cachedEmployee as any;
    }
  }

  // Cache employee if present in pre-order
  if (preOrder.employee) {
    queryClient.setQueryData(
      employeeKeys.detail(preOrder.employee.id as UUID),
      preOrder.employee,
    );
  }

  // Cache buyer if present
  if (preOrder.buyer) {
    // Could cache user data here if we have user keys
    // For now, just partner and employee caching
  }
}

/**
 * Helper function to cache multiple pre-orders and their related data
 */
export function cachePreOrders(
  queryClient: ReturnType<typeof useQueryClient>,
  preOrders: PreOrder[],
) {
  preOrders.forEach((preOrder) => {
    // Cache individual pre-order
    queryClient.setQueryData(
      preOrderKeys.detail(preOrder.id as UUID),
      preOrder,
    );

    // Cache related entities
    cachePreOrderRelatedData(queryClient, preOrder);
  });
}

/**
 * Enrich lean recap pre-orders with data from global caches.
 * The backend recap endpoint returns IDs only (no embedded objects) for performance.
 * This function adds partner, product, and unit objects from already-cached global data
 * so downstream components work without changes.
 */
export function enrichRecapPreOrders(
  queryClient: ReturnType<typeof useQueryClient>,
  preOrders: any[],
): void {
  // Build lookup maps from global caches
  const globalPartners =
    queryClient.getQueryData<PartnerResponse[]>(["partners", "global"]) || [];
  const globalProducts =
    queryClient.getQueryData<ProductResponse[]>(["products", "global"]) || [];
  const globalUnits =
    queryClient.getQueryData<UnitResponse[]>(unitKeys.list()) || [];
  const globalLocations =
    queryClient.getQueryData<LocationResponse[]>(["locations", "global"]) || [];

  const partnerMap = new Map(globalPartners.map((p) => [p.id, p]));
  const productMap = new Map(globalProducts.map((p) => [p.id, p]));
  const unitMap = new Map(globalUnits.map((u) => [u.id, u]));
  const locationMap = new Map(globalLocations.map((l) => [l.id, l]));

  for (const preOrder of preOrders) {
    // Enrich partner when missing OR when only a partial shell is present
    // (id/name/code with no `addresses`/`contacts`). The global cache loads
    // with `include_addresses: true`, so genuinely empty partners surface as
    // `[]`, never `undefined` — `addresses === undefined` cleanly detects
    // shells without false positives.
    const existingPartner = preOrder.partner as
      | { addresses?: unknown }
      | null
      | undefined;
    const isShell =
      !!existingPartner && existingPartner.addresses === undefined;
    if (preOrder.partner_id && (!preOrder.partner || isShell)) {
      const fromGlobal = partnerMap.get(preOrder.partner_id);
      if (fromGlobal) {
        preOrder.partner = fromGlobal;
      } else if (!preOrder.partner) {
        // Fallback to individual partner cache only when nothing else is set —
        // the per-partner detail cache may itself hold a shell, so we don't
        // use it to overwrite an existing shell.
        preOrder.partner =
          queryClient.getQueryData(
            partnerKeys.detail(preOrder.partner_id as UUID),
          ) ?? undefined;
      }
    }

    // Enrich shipping_address from partner.addresses
    if (preOrder.shipping_address_id && !preOrder.shipping_address) {
      const partner = preOrder.partner as PartnerResponse | undefined;
      const addr = partner?.addresses?.find(
        (a: any) => a.id === preOrder.shipping_address_id,
      );
      if (addr) {
        preOrder.shipping_address = addr;
      }
    }

    // Enrich employee from cache
    if (preOrder.employee_id && !preOrder.employee) {
      preOrder.employee =
        queryClient.getQueryData(
          employeeKeys.detail(preOrder.employee_id as UUID),
        ) ?? undefined;
    }

    // Enrich location from global cache
    if (preOrder.location_id && !preOrder.location) {
      preOrder.location = locationMap.get(preOrder.location_id) ?? undefined;
    }

    // Enrich flows
    const flows: PreOrderFlow[] = preOrder.flows || [];
    for (const flow of flows) {
      // Enrich product
      if (flow.product_id && !flow.product) {
        flow.product = productMap.get(flow.product_id as UUID) ?? undefined;
      }

      // Enrich unit
      if (flow.unit_id && !flow.unit) {
        flow.unit = unitMap.get(flow.unit_id as UUID) ?? undefined;
      }
    }
  }
}
